/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useQuery } from '@tanstack/react-query';

// Lib
import { apiClient } from '@lib/api-client';
import { QueryConfig } from '@lib/react-query';

// Types

import { TMesurabilityProject } from '@features/offsets/types';
import { useAuthorization } from '@lib/authorization';
import { ROLES } from '@config/constants/roles';

/* -------------------------------------------------------------------------- */
/*                                     Type                                   */
/* -------------------------------------------------------------------------- */
export type TApiResponse = {
	total: number;
	page: number;
	pageSize: number;
	totalPages: number;
	rows: TMesurabilityProject[];
};

type TUseGetAllMeasurabilitiesOptions = {
	queryConfig?: QueryConfig<typeof getMeasurabilities>;
};

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const getMeasurabilities =
	(): Promise<TApiResponse> => {
		return apiClient.get('/measurabilities', {
			params: {
				fields: ['id', 'name'],
			},
		});
	};
export const getPublicMeasurabilities =
	(): Promise<TApiResponse> => {
		return apiClient.get('/public/measurabilities', {
			params: {
				fields: ['id', 'name'],
			},
		});
	};

/* -------------------------------------------------------------------------- */
/*                                     Hook                                   */
/* -------------------------------------------------------------------------- */
export const useGetAllMeasurabilities = ({
	queryConfig,
}: TUseGetAllMeasurabilitiesOptions) => {
	const { checkRole } = useAuthorization();

	const isUserEnabled = checkRole([
		ROLES.PRODUCER_ADMIN,
		ROLES.PRODUCER_STAFF,
		ROLES.ORGANIZATION_ADMIN,
		ROLES.ORGANIZATION_STAFF,
	]);

	const { data: measurabilities, ...rest } = useQuery({
		queryKey: ['all-measurabilities-items'],
		queryFn: () => {
			if (
				!checkRole([
					ROLES.ADMIN,
					ROLES.SUPERADMIN,
					ROLES.STAFF,
					ROLES.PRODUCER_ADMIN,
					ROLES.PRODUCER_STAFF,
					ROLES.ORGANIZATION_ADMIN,
					ROLES.ORGANIZATION_STAFF,
				])
			) {
				return getPublicMeasurabilities();
			}
			return getMeasurabilities();
		},
		...queryConfig,
		enabled: isUserEnabled,
		staleTime: 0,
	});

	const idsMeasurabilities = Array.isArray(
		measurabilities?.rows
	)
		? measurabilities?.rows?.map(
				(measurability) => measurability.id
			)
		: [];

	return {
		data: measurabilities?.rows,
		idsMeasurabilities,
		...rest,
	};
};
